import {ContextToolbar, MenuItem} from './DomMenu';
import { keyname } from './util/DomUtils';
import { SearchMatcher, secondsElapsedSince } from './util/CoreUtils';
import { PropertyObject} from './lib/memoTypes';
import $ from 'jquery';
import {ExprError, Expression, PureExpressionResult} from "./util/expr/Expr";
import {UiContext} from "./util/UiContext";

export interface DynamicToolbarSource {
    getToolbarItemDefs(): Readonly<Command[]>;
}
export type CommandProperties = PropertyObject & {
    toolbar?: string,
    showIfAny?: string, showIfAll?: string,
    pos?: number,
    self?, param?: Expression | PureExpressionResult, param2?: Expression | PureExpressionResult,
    click?: string | string[], clickItemMethod?: string, clickSelectionMethod?: string, clickMemoMethod?: string, clickAppMethod?: string, clickSelfMethod?: string,
};
export type EvaluatableParams = {
    evaluate: (ctx: Record<string, PureExpressionResult>) => PureExpressionResult[],
    toString: () => string,
}
export class Action {
    static readonly NULL_PARAMS = exprFromParams("null", "null");

    constructor(public readonly mode: string | undefined,
                public readonly objId: string | undefined,
                public readonly objRef: any | undefined,
                public readonly method: string,
                public readonly params: EvaluatableParams) {
        if (!objId && !objRef) throw new Error("Action needs either objId or objRef");
        if (objId && objRef) throw new Error("Action must have only one of objId or objRef");
        if (!method) throw new Error("Action must have method");
        if (!params) throw new Error("Action must have params");
    }

    static parse(click: string, selfReference?: any): Action {
        const match = /^\s*(?:(?<mode>\w+):\s*)?(?<obj>\w+)\.(?<method>[\w-]+)\b(?<params>.*)\s*$/.exec(click);
        const params = new Expression('[' + match.groups['params'] + ']') as EvaluatableParams;
        let objId = match.groups['obj'], objRef = undefined;
        if (objId === 'self') { objId = undefined; objRef = selfReference; if (!objRef) throw new Error(`Action "${click} needs selfReference, but ${typeof selfReference} provided`) }
        return new Action(match.groups['mode'], objId, objRef, match.groups['method'], params);
    }

    static lambda(lambda: Function) {
        return new Action('passTarget', undefined, { lambda }, 'lambda', Action.NULL_PARAMS)
    }
}
function exprFromParams(...params): EvaluatableParams {
    params.forEach((param, index) => { if (typeof param !== 'string' && typeof param !== 'undefined') throw new Error(`Illegal param${index} type ${typeof param}`); })
    return new Expression('[' + params.filter(p => typeof p !== 'undefined').join(', ') + ']') as EvaluatableParams;
}

export class Command {
    actions: Action[];
    showIfAny?: SearchMatcher;
    showIfAll?: SearchMatcher;
    pos: number;
    hotkey: string[];
    title?: string; icon?: string;
    id: string;
    constructor(public readonly props: CommandProperties, public readonly selfReference?: any, actions?: Action[]) {
        if (actions) {
            this.actions = actions;
        } else if (props.click) {
            this.actions = (props.click instanceof Array ? props.click : [props.click]).map(x => Action.parse(x, selfReference));
        } else {
            if (props.clickItemMethod) {
                this.actions = [ new Action('loop', 'item', undefined, props.clickItemMethod, exprFromParams(props.param, props.param2)) ];
            } else if (props.clickSelectionMethod) {
                this.actions = [ new Action('passTarget', 'memo', undefined, props.clickSelectionMethod, exprFromParams(props.param, props.param2)) ];
            } else if (props.clickMemoMethod) {
                this.actions = [ new Action(undefined, 'memo', undefined, props.clickMemoMethod, exprFromParams(props.param, props.param2)) ];
            } else if (props.clickAppMethod) {
                this.actions = [ new Action(undefined, 'app', undefined, props.clickAppMethod, exprFromParams(props.param, props.param2)) ];
            } else if (props.clickSelfMethod) {
                if (!selfReference) throw new Error("Command: clickSelfMethod specified but selfReference missing");
                this.actions = [ new Action('passTarget', undefined, selfReference, props.clickSelfMethod, exprFromParams(props.param, props.param2)) ];
            }
        }
        if (props.showIfAny) this.showIfAny = new SearchMatcher(props.showIfAny);
        if (props.showIfAll) this.showIfAll = new SearchMatcher(props.showIfAll);
        if (typeof props.hotkey === 'string') this.hotkey = props.hotkey.split(/;;/); else if (props.hotkey instanceof Array) this.hotkey = props.hotkey; else this.hotkey = [];
        this.pos = +this.props.pos || 9999;
        this.title = this.props.title ? `${this.props.title}` : '';
        this.icon = this.props.icon ? `${this.props.icon}` : undefined;
        this.id = this.props.id;
    }
    static fromProperties(props: CommandProperties, selfReference?: any) {
        return new Command(props, selfReference);
    }
    static lambda(props: CommandProperties, lambda: Function) {
        return new Command(props, undefined, [Action.lambda(lambda)]);
    }
}
export abstract class ActionManager {
    //static ALLOWED_FOR_SCRIPTING = Symbol('ALLOWED_FOR_SCRIPTING');

    toolbars : ContextToolbar[] = [];
    staticCommands : Command[];
    private cachedToolbarItems:{[key:string]: Command[]};
    private cachedToolbarItemsTs = 0;
    private dynamicToolbarSources = new Set<DynamicToolbarSource>();

    public readonly actionTargets : {[key: string]: (method: string) => [any, string]} = { 'app': (method: string) => [UiContext.instance, method] };

    protected constructor(staticToolbarItems: CommandProperties[]) {
        this.staticCommands = staticToolbarItems.map(x => new Command(x));
    }
    addDynamicToolbarSource(src: DynamicToolbarSource) {
        if (!src || !('getToolbarItemDefs' in src)) throw new Error("Invalid dynamic toolbar source");
        this.dynamicToolbarSources.add(src);
    }
    addStaticCommand(command: Command) {
        this.staticCommands.push(command);
    }
    async getDynamicToolbarSources(): Promise<Set<DynamicToolbarSource>> {
        return this.dynamicToolbarSources;
    }
    async getDynamicToolbarItems(): Promise<Readonly<Command>[]> {
        const relevantMemos = await this.getDynamicToolbarSources();
        return [...relevantMemos].flatMap(memo => memo.getToolbarItemDefs());
    }
    async getToolbarItems(): Promise<{[key:string]: Command[]}> {
        if (!this.cachedToolbarItems || secondsElapsedSince(4, this.cachedToolbarItemsTs)) {
            const dynamicItems = await this.getDynamicToolbarItems();
            this.cachedToolbarItems = ActionManager.groupToolbarItems(this.staticCommands.concat(dynamicItems));
            this.cachedToolbarItemsTs = Date.now();
        }
        return this.cachedToolbarItems//.filter();
    }
    static groupToolbarItems(input: Command[]): {[key:string]: Command[]} {
        const dict = {};
        for(const item of input) {
            if (!item.props.toolbar) continue;
            for(const toolbar of item.props.toolbar.split(/,/)) {
                if (!dict[toolbar]) dict[toolbar] = [];
                dict[toolbar].push(item);
            }
        }
        for(const key in dict) dict[key].sort((a, b) => (a.pos - b.pos));
        return dict;
    }
    async buildContextMenu(toolbarName: string, targetItems: { props: PropertyObject }[], event?: MouseEvent, targetObjectType?: string): Promise<MenuItem[]> {
        const menuItems = (await this.getToolbarItems())[toolbarName] || [];
        const selActions = menuItems.filter(x => this.matchesTarget(x, targetItems)).map(x => Object.assign({}, x.props, {
            text: x.title,
            action: () => {
                this.toolbarClick(x, event, targetItems);
            }
        }));
        return selActions;
    }
    async handleKeyEvent(e: KeyboardEvent, targetItems: { props: PropertyObject }[]): Promise<boolean> {
        let key = keyname(e);
        return !!await this.handleHotkeyByName(key, e, targetItems);
    }
    async handleHotkeyByName(key: string, e: KeyboardEvent, targetItems: { props: PropertyObject }[], forceToolbars?: Set<string>) {
        const handler = await this.findHotkeyHandler(key, targetItems, forceToolbars);
        if (handler) {
            e?.preventDefault();
            e?.stopPropagation();
            this.toolbarClick(handler, e, targetItems);
            return true;
        }
    }
    async findHotkeyHandler(key: string, targetItems: { props: PropertyObject }[], forceToolbars?: Set<string>): Promise<any> {
        let toolbars = this.cachedToolbarItems ?? await this.getToolbarItems();
        for(var tbName in toolbars) {
            if (forceToolbars) { if (!forceToolbars.has(tbName)) continue; }
            else if (!this.isToolbarEnabled(tbName, targetItems)) continue;
            var tbItems = toolbars[tbName];
            for (const x of tbItems) {
                if (x.hotkey.indexOf(key) !== -1 && this.matchesTarget(x, targetItems)) {
                    return x;
                }
            }
        }
        return null;
    }
    matchesTarget(toolbarItem: Command, targetItems: { props: PropertyObject }[]) {
        if (toolbarItem.showIfAny) {
            return targetItems.some(item => toolbarItem.showIfAny.isMatch(item.props));
        }
        if (toolbarItem.showIfAll) {
            return targetItems.length && targetItems.every(item => toolbarItem.showIfAll.isMatch(item.props));
        }
        return true;
    }
    abstract isToolbarEnabled(toolbarName: string, targetItems?: { props: PropertyObject }[]): boolean;

    async toolbarClick(cmd: Command, e?: Event, targetItems?: { props: PropertyObject }[], extraContext?: PureExpressionResult): Promise<any> {
        let r: any = undefined;
        for(const action of cmd.actions) {
            r = await this.execute(action, e, targetItems, extraContext);
        }
        return r;
    }

    async execute(action: Action, event, targetItems: any[], extraContext?: PureExpressionResult): Promise<any> {
        console.log("ACTION: ",action.mode,action.objId,action.objRef,action.method,action.params,targetItems)
        const all = targetItems?.map(x => x?.props);
        const mkContext = (item) => Object.assign({}, item?.props, {'$all': all}, extraContext)

        if (action.mode === 'loop' && action.objId === 'item') {
            for (const i of targetItems.slice()) {
                const params = action.params.evaluate(mkContext(i));
                await i[action.method](params[0], params[1], event);
            }
            return;
        }

        const [o, m] = action.objRef ? [action.objRef, action.method] : this.actionTargets[action.objId](action.method);
        if (!o[m]) throw new Error(`Action target ${action.objId||'-ref-'} (${o}) has no method ${action.method} (${m})`)
        switch (action.mode) {
            case 'loop':
                for (const i of targetItems.slice()) {
                    const params = action.params.evaluate(mkContext(i));
                    await o[m](params[0], params[1], event);
                }
                break;
            case 'passTarget': {
                const params = action.params.evaluate(mkContext(targetItems?.[0]));
                return await o[m](params[0], params[1], event, targetItems);
            }
            case undefined: {
                const params = action.params.evaluate(mkContext(targetItems?.[0]));
                return await o[m](params[0], params[1], event);
            }
            default:
                throw new Error("Undefined action mode "+action.mode);
        }
    }

}

